import { useMemo, useRef, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import {
  FIELD_MAX_LENGTH,
  MAX_ITEMS_PER_LIST,
  acceptedForms,
  joinForms as joinTexts,
  mergePairs,
  parseLines,
  repeatedRows,
} from "@vocifuchs/shared";
import { Banner, Button, Card, Field, inputClass } from "./ui.js";
import { ApiError } from "../lib/api.js";
import { scanPhoto } from "../lib/scan.js";
import { errors } from "../copy/errors.js";
import { common } from "../copy/common.js";
import { lists } from "../copy/lists.js";

/**
 * The one place a list is written, per docs/LIST-EDITOR.md §3. Create and edit
 * share it, and the photo scan in M3 becomes a third loader beside paste --
 * never a second correction view.
 */

export interface ListEditorValues {
  title: string;
  langB: "en" | "fr";
  dueDate: string | null;
  sourceHint: string | null;
  items: Array<{ a: string; b: string }>;
}

/**
 * `bs[0]` is the word, everything after it an alternative. Stored as one
 * "cat|feline" string, which is what acceptedForms reads -- but a child never
 * has to type the bar, and on an iPad keyboard they largely cannot
 * (docs/LIST-EDITOR.md §3.2, issue #26).
 */
interface Form {
  key: number;
  text: string;
}

interface Row {
  key: number;
  a: string;
  bs: Form[];
  /** From a scan the model was not sure about; cleared by the first edit. */
  unsure?: boolean;
}

/** The word plus at most three alternatives. The 80-character limit on the
 *  joined string usually bites first, and says so when it does. */
const MAX_FORMS = 4;

function joinForms(bs: readonly Form[]): string {
  return joinTexts(bs.map((form) => form.text));
}

function isBlank(row: Row): boolean {
  return row.a.trim() === "" && joinForms(row.bs) === "";
}

function asPair(row: Row): { a: string; b: string } {
  return { a: row.a, b: joinForms(row.bs) };
}

export interface ListEditorProps {
  mode: "create" | "edit";
  initial?: {
    title?: string;
    langB?: "en" | "fr";
    dueDate?: string | null;
    sourceHint?: string | null;
    items?: Array<{ a: string; b: string }>;
  };
  /** Offer "save and share with the class" -- only meaningful with a class. */
  canShare?: boolean;
  busy?: boolean;
  error?: string | null;
  backTo: string;
  onSubmit: (values: ListEditorValues, visibility: "private" | "class" | null) => void;
  onDelete?: () => void;
}

const LANG_TAG: Record<"en" | "fr", string> = { en: "en-GB", fr: "fr-FR" };
const LANG_LABEL = { en: common.lang.en, fr: common.lang.fr } as const;

/**
 * Cloudflare's own timeout page (HTML, 524/504) or a connection dropped after
 * a long wait means the scan was too slow, so say that rather than the
 * generic sentence (#82). Other unreadable answers stay generic.
 */
function scanFailure(err: unknown): string {
  if (!(err instanceof ApiError)) return errors.failed;
  if (err.code === "scan_dropped") return errors.scanSlow;
  if (err.code === "bad_response" && (err.status === 524 || err.status === 504)) return errors.scanSlow;
  return err.message;
}

let nextKey = 0;
function toRows(items: ReadonlyArray<{ a: string; b: string }>): Row[] {
  return items.map((item) => {
    // Pasted text and older lists carry the bar; it becomes one field each.
    const forms = acceptedForms(item.b);
    return {
      key: nextKey++,
      a: item.a,
      bs: (forms.length > 0 ? forms : [item.b]).map((text) => ({ key: nextKey++, text })),
    };
  });
}
function emptyRow(): Row {
  return { key: nextKey++, a: "", bs: [{ key: nextKey++, text: "" }] };
}

/** Always keep exactly one blank row at the end, so there is nothing to press
 *  to add a word -- you just keep typing. */
function withTrailingBlank(rows: Row[]): Row[] {
  const last = rows[rows.length - 1];
  if (last && last.a.trim() === "" && joinForms(last.bs) === "") return rows;
  return [...rows, emptyRow()];
}

export function ListEditor({
  mode,
  initial,
  canShare = false,
  busy = false,
  error,
  backTo,
  onSubmit,
  onDelete,
}: ListEditorProps) {
  const [title, setTitle] = useState(initial?.title ?? "");
  const [langB, setLangB] = useState<"en" | "fr">(initial?.langB ?? "en");
  const [due, setDue] = useState(initial?.dueDate ?? "");
  const [sourceHint, setSourceHint] = useState(initial?.sourceHint ?? "");
  const [rows, setRows] = useState<Row[]>(() => withTrailingBlank(toRows(initial?.items ?? [])));
  const [paste, setPaste] = useState("");
  // Open the loader when there is nothing to lose, collapsed once words exist.
  const [loaderOpen, setLoaderOpen] = useState((initial?.items?.length ?? 0) === 0);
  const [confirmReplace, setConfirmReplace] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);
  /** What the last scan or paste did: read, skipped as already there. */
  const [loadNote, setLoadNote] = useState<string | null>(null);
  const [scanError, setScanError] = useState<string | null>(null);
  const firstRowRef = useRef<HTMLInputElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const pasteRef = useRef<HTMLTextAreaElement>(null);
  const dueInputRef = useRef<HTMLInputElement>(null);

  // A scan answers after up to a minute and a half; merge into the rows as
  // they are then, not as they were when the photo was taken.
  const rowsRef = useRef(rows);
  rowsRef.current = rows;

  const parsed = useMemo(() => parseLines(paste), [paste]);
  const repeated = useMemo(() => repeatedRows(rows.map(asPair)), [rows]);
  const filled = rows.filter((row) => row.a.trim() !== "" && joinForms(row.bs) !== "");
  const halfFilled = rows.filter(
    (row) => (row.a.trim() === "") !== (joinForms(row.bs) === ""),
  ).length;

  const editRow = (key: number, change: (row: Row) => Row) => {
    setRows((current) =>
      withTrailingBlank(
        current.map((row) => (row.key === key ? { ...change(row), unsure: false } : row)),
      ),
    );
  };

  const updateA = (key: number, value: string) => editRow(key, (row) => ({ ...row, a: value }));

  const updateForm = (key: number, formKey: number, value: string) =>
    editRow(key, (row) => ({
      ...row,
      bs: row.bs.map((form) => (form.key === formKey ? { ...form, text: value } : form)),
    }));

  const addForm = (key: number) =>
    editRow(key, (row) =>
      row.bs.length >= MAX_FORMS
        ? row
        : { ...row, bs: [...row.bs, { key: nextKey++, text: "" }] },
    );

  // Stable keys, so deleting the middle box does not hand the next one's DOM
  // node -- and the caret sitting in it -- to a different word.
  const dropForm = (key: number, formKey: number) =>
    editRow(key, (row) => {
      const bs = row.bs.filter((form) => form.key !== formKey);
      return { ...row, bs: bs.length > 0 ? bs : [{ key: nextKey++, text: "" }] };
    });

  const remove = (key: number) => {
    setRows((current) => withTrailingBlank(current.filter((row) => row.key !== key)));
  };

  const closePaste = () => {
    setPaste("");
    setConfirmReplace(false);
    setLoaderOpen(false);
    setLocalError(null);
    firstRowRef.current?.focus();
  };

  const applyPaste = () => {
    const { added, skipped } = mergePairs([], parsed.pairs);
    setRows(withTrailingBlank(toRows(added)));
    setLoadNote(skipped > 0 ? lists.editor.alreadyThere(skipped) : null);
    closePaste();
  };

  /** «Hinzufügen»: keep the rows, add what is new (issue #83). */
  const appendPaste = () => {
    const kept = rows.filter((row) => !isBlank(row));
    const { added, skipped } = mergePairs(kept.map(asPair), parsed.pairs);
    setRows(withTrailingBlank([...kept, ...toRows(added)]));
    if (added.length === 0) {
      setLoadNote(lists.editor.allThere);
    } else {
      const notes = [lists.editor.pasteAdded(added.length)];
      if (skipped > 0) notes.push(lists.editor.alreadyThere(skipped));
      setLoadNote(notes.join(" "));
    }
    closePaste();
  };

  /**
   * A scan adds to what is there -- a second photo is usually the next page
   * (docs/LIST-EDITOR.md §4), and pairs already in the list are skipped, so
   * overlapping photos are fine (issue #83). Rows the model doubted go to the
   * very top, marked, so they are checked first; the rest keep the page order.
   * Any failure opens paste instead: never a dead end.
   */
  const scanFile = async (file: File) => {
    setScanning(true);
    setLoadNote(null);
    setScanError(null);
    try {
      const result = await scanPhoto(file, langB);
      const toRow = (pair: { a: string; b: string; unsure: boolean }) => ({
        ...toRows([pair])[0]!,
        unsure: pair.unsure,
      });
      const kept = rowsRef.current.filter((row) => !isBlank(row));
      const { added, skipped } = mergePairs(kept.map(asPair), result.pairs);
      const doubtful = added.filter((pair) => pair.unsure).map(toRow);
      const sure = added.filter((pair) => !pair.unsure).map(toRow);
      setRows(withTrailingBlank([...doubtful, ...kept, ...sure]));
      // The title field stays editable while the photo is read.
      if (result.title) setTitle((current) => (current.trim() === "" ? result.title ?? "" : current));
      const notes = [lists.editor.scanDone(result.pairs.length, doubtful.length)];
      if (skipped > 0) notes.push(lists.editor.alreadyThere(skipped));
      if (result.warnings.some((w) => w.startsWith("dropped"))) notes.push(lists.editor.scanDropped);
      if (result.warnings.includes("truncated")) notes.push(lists.editor.scanTruncated);
      setLoadNote(notes.join(" "));
      setLoaderOpen(false);
    } catch (err) {
      setScanError(`${scanFailure(err)} ${lists.editor.scanFallback}`);
      setLoaderOpen(true);
      queueMicrotask(() => pasteRef.current?.focus());
    } finally {
      setScanning(false);
    }
  };

  const requestApply = () => {
    if (parsed.pairs.length === 0) return;
    if (filled.length > 0) {
      setConfirmReplace(true);
      return;
    }
    applyPaste();
  };

  const submit = (visibility: "private" | "class" | null) => {
    setLocalError(null);
    if (title.trim().length < 2) {
      setLocalError(lists.editor.errTitle);
      return;
    }
    if (halfFilled > 0) {
      setLocalError(lists.editor.errHalfRow);
      return;
    }
    if (filled.length === 0) {
      setLocalError(lists.editor.errNeedWord);
      return;
    }
    if (filled.length > MAX_ITEMS_PER_LIST) {
      setLocalError(lists.editor.errTooMany(MAX_ITEMS_PER_LIST));
      return;
    }
    // The limit applies to the joined string, not to a single field, so the
    // error has to point at the row rather than at a box that looks fine.
    const overlong = filled.findIndex((row) => joinForms(row.bs).length > FIELD_MAX_LENGTH.term);
    if (overlong >= 0) {
      setLocalError(lists.editor.errAltTooLong(overlong + 1, FIELD_MAX_LENGTH.term));
      return;
    }
    onSubmit(
      {
        title: title.trim(),
        langB,
        dueDate: due || null,
        sourceHint: sourceHint.trim() || null,
        items: filled.map((row) => ({ a: row.a.trim(), b: joinForms(row.bs) })),
      },
      visibility,
    );
  };

  const shown = localError ?? error ?? null;
  // The «Katze / cat» example only while the list is empty. Next to a scanned
  // row with an unreadable cell it looks like something the model read.
  const showExample = rows.length === 1;

  return (
    <form
      className="space-y-6"
      onSubmit={(event: FormEvent) => {
        event.preventDefault();
        submit(mode === "create" ? "private" : null);
      }}
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <Link
            to={backTo}
            className="inline-flex items-center gap-1 font-semibold text-fox-ink underline-offset-4 hover:underline"
          >
            <span aria-hidden>←</span> {common.back}
          </Link>
          <h1 className="mt-2 text-3xl sm:text-4xl">
            {mode === "create" ? lists.editor.createTitle : lists.editor.editTitle}
          </h1>
        </div>
      </div>

      {shown ? <Banner>{shown}</Banner> : null}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,20rem)_minmax(0,1fr)] lg:gap-8">
        <Card className="h-fit space-y-4 p-5">
          <Field label={lists.editor.title}>
            <input
              className={inputClass()}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              maxLength={60}
              placeholder="Unit 4: Tiere"
            />
          </Field>

          {mode === "create" ? (
            <Field label={lists.editor.language}>
              <select
                className={inputClass()}
                value={langB}
                onChange={(e) => setLangB(e.target.value as "en" | "fr")}
              >
                <option value="en">{common.lang.en}</option>
                <option value="fr">{common.lang.fr}</option>
              </select>
            </Field>
          ) : (
            <div className="space-y-1.5">
              <span className="text-sm font-semibold text-ink-soft">{lists.editor.language}</span>
              <p className="min-h-12 rounded-2xl border-2 border-dashed border-line px-4 py-2.5">
                {LANG_LABEL[langB]}
              </p>
              <span className="block text-sm text-ink-soft">
                {lists.editor.languageLocked}
              </span>
            </div>
          )}

          <details open={due !== ""}>
            <summary className="cursor-pointer list-none text-sm font-semibold text-ink-soft underline-offset-4 hover:underline">
              {lists.editor.exam}
            </summary>
            <div className="min-w-0 space-y-2 pt-2">
              {/* Native date controls ignore w-* on the input; cap the wrapper. */}
              <div className="w-full max-w-[13rem] min-w-0">
                <input
                  ref={dueInputRef}
                  className={`${inputClass()} min-w-0`}
                  type="date"
                  value={due}
                  onChange={(e) => setDue(e.target.value)}
                  aria-label={lists.editor.examAria}
                />
              </div>
              {due ? (
                <button
                  type="button"
                  className="text-sm font-semibold text-ink-soft underline-offset-4 hover:underline"
                  onClick={() => {
                    setDue("");
                    // Keep focus in the field after the clear control unmounts.
                    queueMicrotask(() => dueInputRef.current?.focus());
                  }}
                >
                  {lists.editor.clearExam}
                </button>
              ) : null}
              <span className="block text-sm text-ink-soft">
                {lists.editor.examHint}
              </span>
            </div>
          </details>

          <details open={sourceHint.trim() !== ""}>
            <summary className="cursor-pointer list-none text-sm font-semibold text-ink-soft underline-offset-4 hover:underline">
              {lists.editor.source}
            </summary>
            <div className="pt-2">
              <input
                className={inputClass()}
                value={sourceHint}
                onChange={(e) => setSourceHint(e.target.value)}
                maxLength={60}
                placeholder="Young World 3, Unit 4"
                aria-label={lists.editor.source}
              />
            </div>
          </details>
        </Card>

        <div className="space-y-4">
          <section className="space-y-2">
            <div className="flex items-baseline justify-between gap-3">
              <h2 className="text-xl sm:text-2xl">{lists.editor.words}</h2>
              <p aria-live="polite" className="text-sm text-ink-soft">
                {common.words(filled.length)}
              </p>
            </div>

            <div className="hidden gap-3 px-1 text-sm font-semibold text-ink-soft sm:grid sm:grid-cols-[1fr_1fr_3rem]">
              <span>{common.lang.de}</span>
              <span>{LANG_LABEL[langB]}</span>
              <span className="sr-only">{lists.editor.deleteRow}</span>
            </div>

            {loadNote ? (
              <p aria-live="polite" className="rounded-2xl border border-line bg-paper-2 px-4 py-3 text-sm">
                {loadNote}
              </p>
            ) : null}

            <ul className="space-y-2">
              {rows.map((row, index) => {
                const isLast = index === rows.length - 1;
                const blank = isBlank(row);
                const canAdd = !blank && row.bs.length < MAX_FORMS;
                return (
                  <li
                    key={row.key}
                    className={`grid gap-2 rounded-2xl border p-2 sm:grid-cols-[1fr_1fr_3rem] sm:items-start sm:gap-3 ${
                      row.unsure
                        ? "border-amber/60 bg-amber/10"
                        : "border-line/60 bg-paper-3 sm:border-0 sm:bg-transparent sm:p-0"
                    }`}
                  >
                    {/* Never colour alone (docs/UX.md §8): the row says so too. */}
                    {row.unsure ? (
                      <span className="text-sm font-semibold text-amber sm:col-span-3">
                        {lists.editor.unsureRow}
                      </span>
                    ) : null}
                    {/* A hint only: the child decides whether to delete it. */}
                    {repeated.has(index) ? (
                      <span className="text-sm text-ink-soft sm:col-span-3">{lists.editor.repeatedRow}</span>
                    ) : null}
                    <input
                      ref={index === 0 ? firstRowRef : undefined}
                      className={inputClass()}
                      value={row.a}
                      onChange={(e) => updateA(row.key, e.target.value)}
                      maxLength={80}
                      placeholder={showExample ? "Katze" : ""}
                      aria-label={lists.editor.deAria(index + 1)}
                      autoComplete="off"
                    />

                    {/* One box per accepted answer. The first is the word, the
                        rest are alternatives -- no bar to type anywhere. */}
                    <div className="space-y-2">
                      {row.bs.map((form, formIndex) => (
                        <div key={form.key} className="flex gap-2">
                          <input
                            className={inputClass()}
                            value={form.text}
                            onChange={(e) => updateForm(row.key, form.key, e.target.value)}
                            maxLength={80}
                            lang={LANG_TAG[langB]}
                            placeholder={showExample && formIndex === 0 ? "cat" : ""}
                            aria-label={
                              formIndex === 0
                                ? lists.editor.langAria(LANG_LABEL[langB], index + 1)
                                : lists.editor.altAria(index + 1, formIndex)
                            }
                            autoComplete="off"
                            spellCheck={false}
                          />
                          {formIndex > 0 ? (
                            <button
                              type="button"
                              onClick={() => dropForm(row.key, form.key)}
                              className="min-h-12 shrink-0 rounded-2xl border-2 border-line px-3 font-semibold text-ink-soft hover:border-rose/50 hover:text-rose"
                              aria-label={lists.editor.dropAltAria(index + 1, formIndex)}
                            >
                              <span aria-hidden>×</span>
                            </button>
                          ) : null}
                        </div>
                      ))}
                      {canAdd ? (
                        <button
                          type="button"
                          onClick={() => addForm(row.key)}
                          className="min-h-11 w-full text-left text-sm font-semibold text-fox-ink underline-offset-4 hover:underline"
                        >
                          {lists.editor.addAlt}
                        </button>
                      ) : null}
                    </div>

                    {isLast && blank ? (
                      <span className="hidden sm:block" />
                    ) : (
                      <button
                        type="button"
                        onClick={() => remove(row.key)}
                        className="min-h-12 rounded-2xl border-2 border-line px-4 font-semibold text-ink-soft hover:border-rose/50 hover:text-rose sm:px-0"
                        aria-label={lists.editor.deleteRowAria(index + 1)}
                      >
                        <span aria-hidden>×</span>
                        <span className="ml-2 sm:hidden">{lists.editor.deleteRow}</span>
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>

            <p className="text-sm text-ink-soft">{lists.editor.altHint}</p>
          </section>

          <details
            open={loaderOpen}
            onToggle={(e) => setLoaderOpen((e.currentTarget as HTMLDetailsElement).open)}
          >
            <summary className="cursor-pointer list-none font-semibold text-fox-ink underline-offset-4 hover:underline">
              {lists.editor.quickLoad}
            </summary>
            <div className="space-y-3 pt-3">
              <div className="space-y-2">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  className="sr-only"
                  tabIndex={-1}
                  aria-hidden
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    e.target.value = "";
                    if (file) void scanFile(file);
                  }}
                />
                <div className="sm:max-w-xs">
                  <Button
                    type="button"
                    variant="ghost"
                    disabled={scanning}
                    onClick={() => fileRef.current?.click()}
                  >
                    <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5" aria-hidden>
                      <path
                        d="M4 8.5h3l1.6-2.5h6.8L17 8.5h3v10H4z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinejoin="round"
                      />
                      <circle cx="12" cy="13" r="3.2" stroke="currentColor" strokeWidth="2" />
                    </svg>
                    {scanning ? lists.editor.scanning : lists.editor.scan}
                  </Button>
                </div>
                <p aria-live="polite" className="text-sm text-ink-soft">
                  {scanning ? lists.editor.scanWait : lists.editor.scanHint(LANG_LABEL[langB])}
                </p>
                {scanError ? <Banner>{scanError}</Banner> : null}
              </div>

              <Field label={lists.editor.paste} hint={lists.editor.pasteHint}>
                <textarea
                  ref={pasteRef}
                  className={`${inputClass()} min-h-40 py-3 font-mono text-sm leading-relaxed`}
                  value={paste}
                  onChange={(e) => {
                    setPaste(e.target.value);
                    setConfirmReplace(false);
                  }}
                  placeholder={"Katze = cat\nHund = dog"}
                />
              </Field>
              <p aria-live="polite" className="min-h-5 text-sm text-ink-soft">
                {parsed.pairs.length > 0
                  ? lists.editor.recognized(
                      parsed.pairs.length,
                      parsed.skippedLines.length
                        ? parsed.skippedLines.join(", ")
                        : undefined,
                    )
                  : ""}
              </p>
              {confirmReplace ? (
                <div className="space-y-2 rounded-2xl border-2 border-dashed border-amber/50 p-3">
                  <p>
                    {lists.editor.replaceConfirm(
                      common.words(filled.length),
                    )}
                  </p>
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <Button type="button" variant="ghost" onClick={appendPaste}>
                      {lists.editor.append}
                    </Button>
                    <Button type="button" variant="ghost" onClick={applyPaste}>
                      {lists.editor.replace}
                    </Button>
                    <Button type="button" variant="ghost" onClick={() => setConfirmReplace(false)}>
                      {common.cancel}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="sm:max-w-xs">
                  <Button
                    type="button"
                    variant="ghost"
                    disabled={parsed.pairs.length === 0}
                    onClick={requestApply}
                  >
                    {lists.editor.apply}
                  </Button>
                </div>
              )}
            </div>
          </details>

          <div className="flex flex-col gap-2 sm:max-w-xs">
            <Button type="submit" disabled={busy}>
              {busy ? common.saving : common.save}
            </Button>
            {mode === "create" && canShare ? (
              <Button
                type="button"
                variant="ghost"
                disabled={busy}
                onClick={() => submit("class")}
              >
                {lists.editor.saveAndShare}
              </Button>
            ) : null}
            {mode === "edit" && onDelete ? (
              <button
                type="button"
                onClick={onDelete}
                disabled={busy}
                className="min-h-12 rounded-2xl px-4 font-semibold text-rose underline-offset-4 hover:underline disabled:opacity-50"
              >
                {lists.editor.deleteList}
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </form>
  );
}
